import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Car, 
  Users, 
  Calendar, 
  FileText, 
  Shield, 
  Clock, 
  Award, 
  ChevronRight,
  Bike,
  Truck,
  Bus,
  Caravan,
  Video,
  BookOpen,
  Target,
  CheckCircle,
  Play,
  BarChart3,
  Smartphone,
  Crown,
  Camera,
  X,
  CreditCard,
  Monitor
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: Users,
    title: "Instructori Profesioniști",
    description: "Echipă experimentată și răbdătoare, dedicată succesului tău"
  },
  {
    icon: Calendar,
    title: "Program Flexibil",
    description: "Adaptăm orele de conducere la programul tău"
  },
  {
    icon: FileText,
    title: "Pregătire Teoretică",
    description: "Cursuri complete pentru examenul teoretic"
  },
  {
    icon: Shield,
    title: "Sprijin Complet",
    description: "Te ajutăm cu fișele medicale și avizele psihologice"
  },
  {
    icon: Car,
    title: "Mașini Moderne",
    description: "Flotă nouă pentru siguranță și confort"
  },
  {
    icon: Award,
    title: "Rată Mare de Succes",
    description: "Metode dovedite pentru promovarea examenului"
  },
  {
    icon: CreditCard,
    title: "Plată în Rate",
    description: "Posibilitate de plată în rate pentru accesibilitate maximă"
  },
  {
    icon: Monitor,
    title: "Simulator Auto 360°",
    description: "Simulator de ultimă generație pentru pregătire practică avansată"
  }
];

const categories = [
  { code: "B", name: "Cutie Manuală", icon: Car, color: "from-blue-500 to-blue-700" },
  { code: "B Auto", name: "Cutie Automată", icon: Car, color: "from-cyan-500 to-blue-600" },
  { code: "B Extins", name: "Fără Examene", icon: Car, color: "from-green-500 to-emerald-600" },
  { code: "B+E", name: "Cu Remorcă", icon: Caravan, color: "from-teal-500 to-cyan-600" },
  { code: "C", name: "Camion", icon: Truck, color: "from-amber-500 to-orange-600" },
  { code: "C+E", name: "TIR", icon: Truck, color: "from-slate-600 to-slate-800" },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

// FAQ Item Component
function FaqItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="px-6 py-5 cursor-pointer" onClick={() => setIsOpen(!isOpen)}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-slate-900">{question}</h3>
        <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </div>
      {isOpen && (
        <motion.p 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-3 text-slate-600"
        >
          {answer}
        </motion.p>
      )}
    </div>
  );
}

const platformFeatures = [
  {
    icon: Video,
    title: "Cursuri Video",
    description: "Lecții pe capitole cu explicații clare de la instructori",
    color: "bg-purple-100 text-purple-600"
  },
  {
    icon: FileText,
    title: "Teste DRPCIV",
    description: "Chestionare oficiale",
    color: "bg-blue-100 text-blue-600"
  },
  {
    icon: BarChart3,
    title: "Progres Personal",
    description: "Statistici detaliate, vezi unde greșești și îmbunătățește-te",
    color: "bg-green-100 text-green-600"
  }
];

const steps = [
  { number: "1", title: "Creează cont", description: "Gratuit, în 30 secunde" },
  { number: "2", title: "Alege abonament", description: "" },
  { number: "3", title: "Începe să înveți", description: "Video + Teste" }
];

const benefits = [
  "Chestionare identice cu cele de la examen",
  "Video-uri explicative pentru fiecare capitol",
  "Învață oriunde, de pe telefon sau PC",
  "Urmărește-ți progresul în timp real"
];

export default function HomePage() {
  const navigate = useNavigate();
  const [publicStats, setPublicStats] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/stats`).then(res => setPublicStats(res.data)).catch(() => {});
    axios.get(`${API}/gallery`).then(res => setGalleryImages(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-slate-50 hero-pattern noise-overlay" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider">
                <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse" />
                Deschidere În Curând
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-slate-900 tracking-tighter leading-[1.1] font-['Archivo']">
                Învață Legislația Rutieră{" "}
                Logic.{" "}
                De la <span className="text-red-600">0</span> la <span className="text-green-600">Admis</span>.
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl">
                Cursuri video clare, explicații logice, exemple reale și teste identice cu examenul DRPCIV.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#cum-functioneaza">
                  <Button 
                    data-testid="hero-cta-primary"
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-8 py-6 text-lg shadow-lg shadow-blue-600/25 transform hover:-translate-y-1 transition-all btn-shine"
                  >
                    Începe Cursul
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
                <Link to="/lectii-gratuite">
                  <Button 
                    data-testid="hero-cta-secondary"
                    size="lg" 
                    variant="outline"
                    className="border-2 border-slate-200 hover:border-blue-600 text-slate-900 font-semibold px-8 py-6 text-lg"
                  >
                    Vezi Lecțiile Gratuite
                  </Button>
                </Link>
              </div>
              
              {/* Stats */}
              <div className="flex gap-8 pt-8 border-t border-slate-200">
                <div>
                  <p className="text-3xl font-black text-slate-900 font-['Archivo']">6</p>
                  <p className="text-sm text-slate-500">Categorii Permis</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-slate-900 font-['Archivo']">100%</p>
                  <p className="text-sm text-slate-500">Dedicare</p>
                </div>
                <div>
                  <p className="text-3xl font-black text-slate-900 font-['Archivo']">24/7</p>
                  <p className="text-sm text-slate-500">Suport</p>
                </div>
              </div>
            </motion.div>
            
            {/* Hero Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative z-10">
                <img
                  src="/images/pozahero.jpg"
                  alt="Student șofer fericit"
                  className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/5]"
                />
                {/* Floating Card */}
                <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <Award className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-bold text-slate-900">Instructori Certificați</p>
                      <p className="text-sm text-slate-500">Experiență dovedită</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Background decoration */}
              <div className="absolute top-8 right-8 w-full h-full bg-blue-600/10 rounded-2xl -z-10" />
              <div className="absolute top-4 right-4 w-full h-full border-2 border-yellow-400/30 rounded-2xl -z-20" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Platform Features - ÎNVAȚĂ ONLINE */}
      <section className="py-16 bg-gradient-to-b from-white to-blue-50" data-testid="platform-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Play className="w-4 h-4" />
              Platformă Online
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight font-['Archivo']">
              Învață Online, Oriunde Ești
            </h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              Pregătește-te pentru examenul auto cu platforma noastră modernă
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {platformFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full border-0 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-white">
                  <CardContent className="p-8 text-center">
                    <div className={`w-16 h-16 ${feature.color} rounded-2xl flex items-center justify-center mx-auto mb-5`}>
                      <feature.icon className="w-8 h-8" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works - CUM FUNCȚIONEAZĂ */}
      <section id="cum-functioneaza" className="py-16 bg-white" data-testid="how-it-works">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight font-['Archivo']">
              Cum Funcționează?
            </h2>
            <p className="text-slate-600 mt-3">Începe în 3 pași simpli</p>
          </motion.div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                className="flex items-center gap-4"
              >
                <div className="relative">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    {step.number}
                  </div>
                </div>
                <div className="text-left">
                  <h3 className="font-bold text-slate-900">{step.title}</h3>
                  <p className="text-sm text-slate-500">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <ChevronRight className="hidden md:block w-6 h-6 text-slate-300 mx-2" />
                )}
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-10"
          >
            <Button 
              size="lg"
              onClick={() => navigate("/login")}
              className="bg-blue-600 hover:bg-blue-700 font-semibold px-8"
            >
              Începe Gratuit
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Why AUTO CORA - DE CE NOI */}
      <section className="py-16 bg-slate-900 text-white" data-testid="why-us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-blue-400 font-semibold text-sm uppercase tracking-wider">De Ce AUTO CORA?</span>
              <h2 className="text-3xl md:text-4xl font-bold mt-3 tracking-tight font-['Archivo']">
                Totul pentru Succesul Tău la Examen
              </h2>
              <div className="mt-8 space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-300">{benefit}</span>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 flex gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate("/login")} 
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  Creează Cont Gratuit
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  onClick={() => navigate("/servicii")}
                  className="border-slate-600 text-white hover:bg-slate-800"
                >
                  Vezi Serviciile
                </Button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <div className="bg-slate-800 rounded-2xl p-6 text-center">
                <div className="text-4xl font-black text-blue-400 font-['Archivo']">{publicStats?.total_users || "100"}+</div>
                <div className="text-slate-400 mt-1">Cursanți</div>
              </div>
              <div className="bg-slate-800 rounded-2xl p-6 text-center">
                <div className="text-4xl font-black text-green-400 font-['Archivo']">6</div>
                <div className="text-slate-400 mt-1">Categorii Permis</div>
              </div>
              <div className="bg-slate-800 rounded-2xl p-6 text-center">
                <div className="text-4xl font-black text-purple-400 font-['Archivo']">{publicStats?.total_videos || "15"}+</div>
                <div className="text-slate-400 mt-1">Lecții Video</div>
              </div>
              <div className="bg-slate-800 rounded-2xl p-6 text-center">
                <div className="text-4xl font-black text-yellow-400 font-['Archivo']">{publicStats?.pass_rate || "95"}%</div>
                <div className="text-slate-400 mt-1">Rată Promovare</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Gallery Preview Section */}
      {galleryImages.length > 0 && (
        <section className="py-20 md:py-28 bg-white" data-testid="gallery-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <span className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <Camera className="w-4 h-4" />
                Galerie Foto
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 tracking-tight font-['Archivo']">
                Descoperă AUTO CORA
              </h2>
              <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
                Privește cum arată lecțiile practice, mașinile noastre moderne și atmosfera de la școală
              </p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {galleryImages.slice(0, 4).map((image, index) => (
                <motion.div 
                  key={image.id} 
                  variants={itemVariants}
                  className="group relative aspect-square rounded-xl overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-all"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image.image_url}
                    alt={image.title || `Galerie ${index + 1}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-4 left-4 right-4">
                      {image.title && (
                        <p className="text-white font-semibold text-sm truncate">{image.title}</p>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mt-10"
            >
              <Link to="/galerie">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8"
                >
                  Vezi Toată Galeria
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Image Lightbox */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" />
          </button>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-4xl max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.image_url}
              alt={selectedImage.title || "Gallery image"}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            {(selectedImage.title || selectedImage.description) && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
                {selectedImage.title && (
                  <h3 className="text-white text-xl font-bold">{selectedImage.title}</h3>
                )}
                {selectedImage.description && (
                  <p className="text-white/80 mt-1">{selectedImage.description}</p>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Pricing Preview - ABONAMENTE - 3 Plans */}
      <section className="py-20 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden" data-testid="pricing-preview">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 bg-blue-500/20 text-blue-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Crown className="w-4 h-4" />
              Abonamente
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight font-['Archivo']">
              Alege Planul Potrivit
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Start Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
            >
              <Card className="h-full bg-gradient-to-br from-slate-700/50 to-slate-600/30 border-slate-500/30 overflow-hidden">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-slate-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-slate-300" />
                  </div>
                  <h3 className="text-2xl font-bold text-white font-['Archivo'] mb-2">Start</h3>
                  <p className="text-slate-400 text-sm mb-4">Acces limitat pentru începători</p>
                  <div className="mb-6">
                    <span className="text-4xl font-black text-green-400 font-['Archivo']">GRATUIT</span>
                  </div>
                  <ul className="text-left space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Lecții gratuite demo
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Acces limitat la teorie
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Chestionare demo
                    </li>
                    <li className="flex items-center gap-3 text-slate-500 line-through">
                      <X className="w-5 h-5 text-red-400/50 flex-shrink-0" />
                      Teorie completă
                    </li>
                    <li className="flex items-center gap-3 text-slate-500 line-through">
                      <X className="w-5 h-5 text-red-400/50 flex-shrink-0" />
                      Video explicații
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-slate-600 hover:bg-slate-500 text-white"
                    onClick={() => navigate("/register")}
                  >
                    Activează Gratuit
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Standard Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="h-full bg-gradient-to-br from-blue-900/50 to-blue-800/30 border-blue-500/30 overflow-hidden">
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-8 h-8 text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white font-['Archivo'] mb-2">Standard</h3>
                  <p className="text-slate-400 text-sm mb-4">Acces complet la teorie și teste</p>
                  <div className="mb-6 flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-black text-white font-['Archivo']">35</span>
                    <span className="text-slate-400">RON</span>
                  </div>
                  <ul className="text-left space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Toată teoria completă
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Chestionar după fiecare capitol
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Chestionare DRPCIV în platformă
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Progres salvat
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Acces 30 zile
                    </li>
                    <li className="flex items-center gap-3 text-slate-500 line-through">
                      <X className="w-5 h-5 text-red-400/50 flex-shrink-0" />
                      Video explicații instructor
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => navigate("/register")}
                  >
                    Alege Standard - 35 RON
                  </Button>
                </CardContent>
              </Card>
            </motion.div>

            {/* Premium Plan */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="md:scale-105"
            >
              <Card className="h-full bg-gradient-to-br from-yellow-900/40 to-amber-800/30 border-yellow-500/40 overflow-hidden relative">
                <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                  RECOMANDAT
                </div>
                <CardContent className="p-8 text-center">
                  <div className="w-16 h-16 bg-yellow-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Crown className="w-8 h-8 text-yellow-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-white font-['Archivo'] mb-2">Premium</h3>
                  <p className="text-slate-400 text-sm mb-4">Acces complet cu video explicații</p>
                  <div className="mb-6 flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-black text-white font-['Archivo']">59</span>
                    <span className="text-slate-400">RON</span>
                  </div>
                  <ul className="text-left space-y-3 mb-6">
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Toată teoria completă
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Chestionar după fiecare capitol
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Chestionare DRPCIV în platformă
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Video explicații
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Lecții video instructor
                    </li>
                    <li className="flex items-center gap-3 text-slate-300">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                      Progres salvat • Acces 30 zile
                    </li>
                  </ul>
                  <Button 
                    className="w-full bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-600 hover:to-amber-700 text-black font-bold"
                    onClick={() => navigate("/register")}
                  >
                    Alege Premium - 59 RON
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Info Footer */}
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 rounded-full px-6 py-3">
              <Shield className="w-5 h-5 text-blue-400" />
              <span className="text-slate-400 text-sm">
                Plăți sigure • Activare instantă pentru planul gratuit • Suport 24/7
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section - Întrebări frecvente */}
      <section className="py-20 bg-slate-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight font-['Archivo']">
              Întrebări frecvente
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="divide-y divide-slate-200 overflow-hidden">
              {[
                {
                  question: "De ce sa platesc?",
                  answer: "Platforma noastră oferă conținut de calitate, actualizat constant cu cele mai noi întrebări DRPCIV, video explicații profesionale și suport dedicat pentru pregătirea examenului auto."
                },
                {
                  question: "O sa fiu taxat din nou cand se termina perioada de acces premium?",
                  answer: "Nu, nu există taxare automată. La expirarea abonamentului, trebuie să reînnoiești manual dacă dorești să continui accesul premium."
                },
                {
                  question: "Preturile sunt afisate in RON, pot plati cu card de euro?",
                  answer: "Da, poți plăti cu orice card (RON sau EUR). Conversia se face automat de către banca ta la cursul zilei."
                },
                {
                  question: "Am mai multe intrebari. Cum va pot contacta?",
                  answer: "Ne poți contacta prin email, telefon sau prin formularul de contact din pagina Contact. Echipa noastră îți răspunde în cel mai scurt timp posibil."
                }
              ].map((faq, index) => (
                <FaqItem key={index} question={faq.question} answer={faq.answer} />
              ))}
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">De Ce Să Ne Alegi</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 tracking-tight font-['Archivo']">
             Avantajele Școlii Auto CORA
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              La AUTO CORA, îți oferim toate resursele pentru a deveni un șofer sigur și încrezător
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card className="h-full border border-slate-200 hover:border-blue-500/50 hover:shadow-xl transition-all duration-300 card-hover">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6">
                      <feature.icon className="w-7 h-7 text-blue-600" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3 font-['Archivo']">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Categories Preview Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">Categorii Disponibile</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-4 tracking-tight font-['Archivo']">
              Alege Categoria Ta
            </h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Oferim cursuri pentru toate categoriile de permis de conducere
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
          >
            {categories.map((cat, index) => (
              <motion.div key={cat.code} variants={itemVariants}>
                <Link to="/servicii" data-testid={`category-card-${cat.code}`}>
                  <Card className="group cursor-pointer h-full border-0 overflow-hidden bento-card">
                    <CardContent className={`p-6 bg-gradient-to-br ${cat.color} text-white h-full flex flex-col items-center justify-center text-center min-h-[160px]`}>
                      <cat.icon className="w-10 h-10 mb-3 opacity-90 group-hover:scale-110 transition-transform" />
                      <p className="text-2xl font-black font-['Archivo']">{cat.code}</p>
                      <p className="text-sm opacity-80 mt-1">{cat.name}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/servicii">
              <Button 
                data-testid="view-all-categories-btn"
                size="lg" 
                variant="outline"
                className="border-2 border-slate-300 hover:border-blue-600 text-slate-900 font-semibold"
              >
                Vezi Toate Detaliile
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-slate-900 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-500 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-yellow-400/20 text-yellow-400 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wider mb-8">
              <Clock className="w-4 h-4" />
              Locuri Limitate
            </div>
            
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight font-['Archivo']">
              Ești Gata Să Pornești La Drum?
            </h2>
            
            <p className="text-slate-400 mt-6 text-lg max-w-2xl mx-auto">
              Nu rata ocazia de a fi printre primii cursanți ai școlii AUTO CORA. 
              Rezervă-ți locul acum și începe pregătirea pentru permisul tău!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <Link to="/programari">
                <Button 
                  data-testid="cta-booking-btn"
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-10 py-6 text-lg shadow-lg shadow-blue-600/25"
                >
                  Rezervă Acum
                  <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  data-testid="cta-contact-btn"
                  size="lg" 
                  variant="outline"
                  className="border-2 border-slate-600 hover:border-white text-white font-semibold px-10 py-6 text-lg bg-transparent hover:bg-white/5"
                >
                  Contactează-ne
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
